<script setup lang="ts">
import {useImageCdn} from "~/composables/useImageCdn";
import {useWadUrls} from "~/composables/useWadUrls";
import {BookmarkType, CompanyStory} from "~/types/constants"
import {useLocalePath} from "#i18n";


const {user} = useAuthUser();
const { fixedWidthImageParams } = useImageCdn()
const localePath = useLocalePath()
const { getPlatformUrl } = useWadUrls()

const {
  story,
  isGhost = false
} = defineProps<{
  story: CompanyStory
  isGhost?: boolean
}>()

const videoSource = computed(() => {
  if (!story.video_id || !story.video_type) return null;

  return story.video_type + '/' + story.video_id;
});

const imageDetails = computed(() => story.images?.at(0))

const companyHq = computed(() => story.company.headquarters?.as_string)

const storyTypeClassSuffix = computed(() => {
  switch (story.cover_type) {
    case CompanyStory.None:
      return 'default';
    case CompanyStory.Image:
      return 'image';
    case CompanyStory.Video:
      return 'video';
    default:
      return '';
  }
})

const router = useRouter();
const storyPath = computed(() =>
    `/companies/${story.company.id}/${story.company.slug}/stories/${story.id}`
)
const onStoryCardClick = () => {
  router.push({ path: storyPath.value })
}
</script>

<template>
  <div v-if="isGhost" class="wad4-company-story-card wad4-company-story-card--ghost">
    <div class="wad4-company-story-card--ghost-content">
      <CommonBookmark
        class="wad4-company-story-card--ghost-bookmark"
        :type="BookmarkType.Story"
        :objectId="story.id"
        variant="light"
        @pointerup.stop
      />
      <svg class="wad4-company-story-card--ghost-svg" xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
        <path d="M54 32C54 26.1652 51.6831 20.5685 47.5573 16.4427C43.4315 12.3169 37.8348 10 32 10C26.1652 10 20.5685 12.3169 16.4427 16.4427C12.3169 20.5685 10 26.1652 10 32C10 34.8891 10.5689 37.7501 11.6745 40.4193C12.7801 43.0884 14.3999 45.5144 16.4427 47.5573C18.4856 49.6001 20.9116 51.2199 23.5807 52.3255C26.2499 53.4311 29.1109 54 32 54C34.8891 54 37.7501 53.4311 40.4193 52.3255C43.0884 51.2199 45.5144 49.6001 47.5573 47.5573C49.6001 45.5144 51.2199 43.0884 52.3255 40.4193C53.4311 37.7501 54 34.8891 54 32ZM32.0495 38C35.7468 38.009 39.2898 39.4839 41.901 42.1016C42.6808 42.8833 42.6796 44.1496 41.8984 44.9297C41.1166 45.7096 39.8504 45.7086 39.0703 44.9271C37.207 43.0589 34.6776 42.0062 32.0391 42H32.0339C30.7152 41.9936 29.4067 42.2501 28.1875 42.7526C26.9688 43.2551 25.8606 43.9937 24.9297 44.9271C24.1496 45.7086 22.8834 45.7095 22.1016 44.9297C21.3204 44.1496 21.3192 42.8833 22.099 42.1016C23.4039 40.7933 24.9556 39.7562 26.6641 39.0521C28.3701 38.349 30.1991 37.9921 32.0443 38H32.0495ZM25 22C26.4423 22 27.208 23.119 27.4948 23.6927C27.8416 24.3866 28 25.2047 28 26C28 26.7953 27.8416 27.6134 27.4948 28.3073C27.208 28.881 26.4423 30 25 30C23.5577 30 22.792 28.881 22.5052 28.3073C22.1584 27.6134 22 26.7953 22 26C22 25.2047 22.1584 24.3866 22.5052 23.6927C22.792 23.119 23.5577 22 25 22ZM39 22C40.4423 22 41.208 23.119 41.4948 23.6927C41.8416 24.3866 42 25.2047 42 26C42 26.7953 41.8416 27.6134 41.4948 28.3073C41.208 28.881 40.4423 30 39 30C37.5577 30 36.792 28.881 36.5052 28.3073C36.1584 27.6134 36 26.7953 36 26C36 25.2047 36.1584 24.3866 36.5052 23.6927C36.792 23.119 37.5577 22 39 22ZM58 32C58 35.4144 57.3275 38.7961 56.0208 41.9505C54.7142 45.1047 52.7996 47.9713 50.3854 50.3854C47.9713 52.7996 45.1047 54.7142 41.9505 56.0208C38.7961 57.3275 35.4144 58 32 58C28.5856 58 25.2039 57.3275 22.0495 56.0208C18.8953 54.7142 16.0287 52.7996 13.6146 50.3854C11.2004 47.9713 9.28578 45.1047 7.97917 41.9505C6.67254 38.7961 6 35.4144 6 32C6 25.1044 8.73864 18.4905 13.6146 13.6146C18.4905 8.73864 25.1044 6 32 6C38.8956 6 45.5095 8.73864 50.3854 13.6146C55.2614 18.4905 58 25.1044 58 32Z" fill="currentColor"/>
      </svg>
      <div class="wad4-company-story-card--ghost-info">
        <h2 class="wad4-company-story-card__heading">
          Sorry, this company story is no longer available
        </h2>
        <nuxt-link class="wad4-company-story-card--ghost-link" :href="getPlatformUrl(`/companies`)">Check out other companies</nuxt-link>
      </div>
    </div>
  </div>
  <article v-else :class="`wad4-company-story-card wad4-company-story-card--${storyTypeClassSuffix}`" @pointerup="onStoryCardClick">
    <header class="wad4-company-story-card__upper">
      <div class="wad4-company-story-card__header">
        <img class="wad4-company-story-card__logo" v-bind="fixedWidthImageParams(story.company.logo_url, 64, 1)" :alt="`${story.company.name} logo`"/>
        <div class="wad4-company-story-card__info">
          <h3 v-if="story.company.name" class="wad4-company-story-card__company-name">
            {{ story.company.name }}
          </h3>
          <p v-if="companyHq" class="wad4-company-story-card__company-hq">
            {{ companyHq }}
          </p>
        </div>
        <CommonBookmark
            :type="'story'"
            :objectId="story.id"
            variant="light"
            @pointerup.stop
        />
      </div>
      <div class="wad4-company-story-card__content">
        <h2 class="wad4-company-story-card__heading">
          <nuxt-link :to="storyPath" @click.stop @pointerup.stop>
            {{ story.title }}
          </nuxt-link>
        </h2>
        <div class="wad4-company-story-card__text wad4__html-content" v-html="story.text"></div>
      </div>
    </header>
    <footer :class="`wad4-company-story-card__footer wad4-company-story-card__footer--${storyTypeClassSuffix}`">
      <ClientOnly>
        <VideoPlayer v-if="story.video_id" class="wad4-company-story-card__video"
            :title="story.title"
            :key="story.video_id"
            :source="videoSource"
            :autoPip="false"
            :autoPlay="false"
            @pointerup.stop
        />
      </ClientOnly>
      <img v-if="imageDetails?.image_url" class="wad4-company-story-card__image"
          v-bind="fixedWidthImageParams(imageDetails.image_url, 480, 1.66)"
          :alt="story.company.name"
      />
    </footer>
  </article>
</template>


<style lang="scss">
.wad4-company-story-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: $wad4-border-radius-xl;
  border: 1px solid $wad4-c-border-border-secondary;
  background: $wad4-c-background-bg-primary;
  box-shadow: $wad4-box-shadow-sm;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    border-radius: 16px;
    border: 1px solid $wad4-c-border-border-secondary;
    background: $wad4-c-background-bg-primary-hover;
    box-shadow: 0 1px 3px 0 $wad4-bc-shadow-neutral-10, 0 1px 2px 0 $wad4-bc-shadow-neutral-6;
    .wad4-company-story-card__name {
      color: $wad4-c-text-text-primary-hover;
    }
  }

  &__upper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $wad4-spacing-xl;
    flex: 1 0 0;
    align-self: stretch;
    padding: 24px 24px 16px 24px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-self: stretch;
    align-items: center;
    gap: $wad4-spacing-md;

    .wad4-bookmark {
      align-self: flex-start;
    }
  }

  &__logo {
    width: 64px;
    height: 64px;
    aspect-ratio: 1/1;
    border-radius: $wad4-border-radius-full;
    border: 1px solid $wad4-c-border-border-subtle;
  }

  &__info {
    display: flex;
    width: 394px;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
  }

  &__heading {
    font-family: $wad4-default-font-unbounded;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 26px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: 0;
    a {
      text-decoration: none;
      color: $wad4-c-text-text-primary;
    }
  }

  &__text {
    color: $wad4-c-text-text-secondary;
    @include text-size-mixin('md');
    display: -webkit-box;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-line-clamp: 13;
    width: 100%;

    & a {
      color: $wad4-c-text-text-link;
      overflow-wrap: break-word;

      &:hover {
        color: $wad4-c-text-text-link-hover
      }
    }
  }

  &--image &__text,
  &--video &__text {
    -webkit-line-clamp: 3;
  }

  &__company-name {
    color: $wad4-c-text-text-secondary;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin: 0;
  }

  &__company-hq {
    color: $wad4-c-text-text-tertiery;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin: 0;
  }

  &__footer {
    display: flex;
    width: 100%;
    border-bottom-left-radius: $wad4-border-radius-xl;
    border-bottom-right-radius: $wad4-border-radius-xl;

    &--image {
      aspect-ratio: 1.66/1;
    }

    &--video {
      aspect-ratio: 16/9;
    }
  }

  &__image {
    align-self: stretch;
    width: 100%;
    height: auto;
    aspect-ratio: 1.66/1;
    display: block;
  }

  &__video {
    align-self: stretch;
    width: 100%;
    height: auto;
    aspect-ratio: 16/9;
    display: block;
  }

  &--ghost {
    background: $wad4-c-background-bg-subtle;
    height: 268px;
    padding: 16px;

    &:hover {
      background: $wad4-c-background-bg-subtle;
    }

    &-svg {
      color: $wad4-c-foreground-fg-disabled;
    }

    .wad4-company-story-card__heading {
      text-align: center;
      color: $wad4-c-text-text-primary;
    }

    &-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      align-self: stretch;
      cursor: default;
    }

    &-bookmark {
      align-self: flex-end;
      background: none;
    }

    &-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    &-link {
      color: $wad4-c-text-text-tertiery;
      font-size: 0.75rem;
    }
  }

}
</style>
